// Photos XOR videos, per project (multi-video v1 product decision).
//
// A project's footage is either a photo sequence that gets COMPILED into an
// mp4, or a set of videos that get CONCATENATED into one — two different
// ingest pipelines producing the same kind of source. Mixing them in one
// project has no defined meaning yet (which photos go where in the video
// timeline?), so v1 refuses the second kind with a friendly message rather
// than silently picking an interpretation.
//
// Centralised here because the rule has to read the same in both directions
// and from more than one surface: the dropzones gate on it, the preflight
// hooks refuse on it, and later phases (the Combine CTA) will too.

export const FOOTAGE_PHOTOS = 'photos';
export const FOOTAGE_VIDEOS = 'videos';

// `incomingKind` is what the user just picked. Returns null when the drop is
// allowed, or `{ kind, existingKind, message }` when it must be refused.
//
// `hasVideos` means multi-video FOOTAGE items (projects/{id}/videos docs from
// the footage ingest). The legacy single source video is passed separately as
// `hasLegacySourceVideo` because it is the same conflict for the user but a
// different sentence: they already have a source video, not a footage set.
export function resolveFootageConflict({
  incomingKind,
  hasPhotos = false,
  hasVideos = false,
  hasLegacySourceVideo = false,
} = {}) {
  if (incomingKind === FOOTAGE_VIDEOS && hasPhotos) {
    return {
      kind: FOOTAGE_VIDEOS,
      existingKind: FOOTAGE_PHOTOS,
      message:
        'This project already holds photos. A project can hold photos or videos, '
        + 'not both — start a new project for these clips.',
    };
  }
  // A project that already has a single source video is refused footage too:
  // that slot is claimed and analysed, and Phase 2's concat output would have
  // to fight it for the same field. Not a photos-vs-videos conflict, so it gets
  // its own sentence.
  if (incomingKind === FOOTAGE_VIDEOS && hasLegacySourceVideo) {
    return {
      kind: FOOTAGE_VIDEOS,
      existingKind: FOOTAGE_VIDEOS,
      message:
        'This project already has a source video. Start a new project to upload a '
        + 'multi-file shoot.',
    };
  }
  if (incomingKind === FOOTAGE_PHOTOS && (hasVideos || hasLegacySourceVideo)) {
    return {
      kind: FOOTAGE_PHOTOS,
      existingKind: FOOTAGE_VIDEOS,
      message: hasLegacySourceVideo && !hasVideos
        ? 'This project already has a source video. A project can hold photos or '
          + 'videos, not both — start a new project for these photos.'
        : 'This project already holds videos. A project can hold photos or videos, '
          + 'not both — start a new project for these photos.',
    };
  }
  return null;
}
